// ChatHeader.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Spacer,
  Button,
  useToast,
  useColorMode,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  Heading,
  Input,
  FormControl,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  HStack,
  ModalFooter,
  ModalHeader,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaSave, FaMapMarkedAlt, FaComments } from "react-icons/fa";
import { Spinner } from "@chakra-ui/react";
import SettingsMenu from "../common/SettingsMenu";
import axiosInstance from "../../helpers/axiosInstance";
import AuthModal from "../AuthModal"; // Import AuthModal
import Translate from "../../components/LanguageTranslation/Translate";

const ChatHeader = ({
  conversationId,
  conversationName,
  fetchMessages,
  startNewConversation,
  isWarningOpen,
  setWarningOpen,
  warningText,
  setWarningText,
  warningFunction,
  setWarningFunction,
  warningErrors,
  setWarningErrors,
  warningLoading,
  setWarningLoading,
  saveAndContinue,
  registerOpenSaveChat,
  onChatSaved,
}) => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false); // State to track save operation
  const [isSignUpOpen, setSignUpOpen] = useState(false); // Add state for AuthModal
  const [isSaveOpen, setSaveOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [errors, setErrors] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteChatId, setDeleteChatId] = useState(null);
  const [deleteChatName, setDeleteChatName] = useState("");

  const fetchOptions = async () => {
    try {
      const response = await axiosInstance.get("/chat/conversation/user");
      setOptions(response.data.data);
      // console.log(options)
      setLoading(false);
    } catch (err) {
      console.error("Error fetching options:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);



  // Expose a function to open the Save Chat modal from parent
  useEffect(() => {
    if (typeof registerOpenSaveChat === "function") {
      registerOpenSaveChat(() => {
        const isAuthenticated = !!localStorage.getItem("REACT_TOKEN_AUTH_KEY");
        if (!isAuthenticated) {
          setSignUpOpen(true);
        } else {
          setErrors("");
          setSaveOpen(true);
        }
      });
    }
  }, [registerOpenSaveChat]);

  const saveChat = async (event) => {
    event.preventDefault();
    let messageBody = {
      name: event.target.name.value,
    };
    try {
      const response = await axiosInstance.post(
        `/chat/conversation/save/${conversationId}`,
        messageBody
      );
      if (response.status === 200) {
        fetchOptions();
        fetchMessages(conversationId);
        setSaveOpen(false);
        toast({
          title: "Chat saved.",
          description: `Your chat was successfully saved.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        if (typeof onChatSaved === "function") {
          onChatSaved(event.target.name.value);
        }
      } else {
        setErrors("You already have a chat with this name");
        // console.log("errors", errors);
      }
    } catch (error) {
      setErrors("You already have a chat with this name");
    }
  };

  const newChat = () => {
    if (conversationName) {
      startNewConversation();
    } else {
      setWarningOpen(true);
      setWarningText(
        "The current chat is not saved and will be lost. Do you wish to continue?"
      );
      setWarningFunction(() => () => {
        startNewConversation();
      });
    }
  };

  const handleOptionChange = (id) => {
    if (!id) return;
    if (conversationName) {
      fetchMessages(id);
    } else {
      setWarningOpen(true);
      setWarningText(
        "The current chat is not saved and will be lost. Do you wish to continue?"
      );
      setWarningFunction(() => () => {
        fetchMessages(id);
      });
    }
  };

  const deleteChat = async (id) => {
    try {
      const response = await axiosInstance.delete(`/chat/conversation/${id}`);
      if (response.status === 200) {
        // If we're deleting the current conversation, start a new one
        if (id === conversationId) {
          startNewConversation();
        }
        fetchOptions();
        setWarningOpen(false);
        toast({
          title: "Chat deleted",
          description: "The chat has been successfully deleted.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast({
        title: "Error",
        description: "Failed to delete the chat. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSaveAndContinue = async (event) => {
    await saveAndContinue(event);
    fetchOptions();
  };

  const openDeleteModal = (chatId, chatName) => {
    setDeleteChatId(chatId);
    setDeleteChatName(chatName);
    setIsDeleteOpen(true);
  };

  const handleSaveTrip = async () => {
    if (!conversationId) {
      toast({
        title: "Error",
        description: "No conversation to save.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true); // Set loading state to true

    try {
      const response = await axiosInstance.post("/bucket-list/save", {
        conversation_id: conversationId,
      });
      // console.log("response", response);
      if (response.data.success) {
        toast({
          title: "Success",
          description: response.data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setSignUpOpen(true); // Open AuthModal on 401 error
      } else if (error.response && error.response.status === 429) {
        toast({
          title: "Error",
          description:
            "Trip saving limit reached. Wait 1 hour before saving another trip",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description:
            error.response.data.message || "Error while saving the trip",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsSaving(false); // Reset loading state
    }
  };

  return (
    <>
      <Box
        flex="1"
        display="flex"
        flexDirection="column"
        pb={1}
        mb={1}
        h="8vh"
        borderBottom="1px"
        borderColor="blackAlpha.200"
        overflowY="hidden"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          bg={colorMode === "dark" ? "gray.700" : "white"}
          width="100%"
          height="100%"
          p={4}
          pt={4}
          pb={4}
          boxSizing="border-box"
          overflowY="hidden"
        >
          {/* Left: Conversation Name and Load Conversation Menu */}

          <HStack alignItems="center">
            <Text
              m={0}
              fontSize={{ base: "md", md: "md" }}
              fontWeight="bold"
              color={colorMode === "dark" ? "gray.200" : "blackAlpha.700"}
            >
              {!conversationName ? (
                <Translate text="New Chat" />
              ) : (
                conversationName
              )}
            </Text>
          </HStack>

          <HStack align="center">
            {/* Right: Settings Menu */}
            <Menu key={options}>
              <MenuButton
                as={Button}
                size={{ base: "xs", md: "sm", lg: "sm" }}
                rightIcon={<ChevronDownIcon />}
              >
                <Translate text="Load conversation" />
              </MenuButton>
              <MenuList>
                {options.map((option) => (
                  <MenuItem
                    key={option._id}
                    display="flex"
                    justifyContent="space-between"
                    onClick={() => handleOptionChange(option._id)}
                  >
                    {option.conversation_name}
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      ml={2}
                      aria-label="Delete Chat"
                      colorScheme="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(option._id, option.conversation_name);
                      }}
                    />
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {/* ───── mobile “more” menu ───── */}
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                size="sm"
                display={{ base: "inline-flex", md: "none" }}
                aria-label="More options"
                _hover="none"
                _active="none"
                _focus="none"
              >
                <BsThreeDotsVertical size={20} />
              </MenuButton>

              <MenuList>
                {/* New Chat */}
                <MenuItem icon={<Icon as={FaComments} />} onClick={newChat}>
                  <Translate text="New Chat" />
                </MenuItem>
                {/* Save Trip */}
                {/* <MenuItem
                  icon={<Icon as={FaMapMarkedAlt} />}
                  isDisabled={!conversationId}
                  onClick={handleSaveTrip}
                >
                  <Translate text="Save Trip" />
                </MenuItem> */}

                {/* Save Chat */}
                <MenuItem
                  icon={<Icon as={FaSave} />}
                  isDisabled={!conversationId}
                  onClick={() => {
                    const isAuthenticated = !!localStorage.getItem(
                      "REACT_TOKEN_AUTH_KEY"
                    );
                    if (!isAuthenticated) {
                      setSignUpOpen(true);
                    } else {
                      setSaveOpen(true);
                      setErrors("");
                    }
                  }}
                >
                  <Translate text="Save Chat" />
                </MenuItem>
              </MenuList>
            </Menu>

            {/* ───── large screen “more” menu ───── */}
            <Menu>
              <MenuButton
                as={Button}
                display={{ base: "none", md: "inline-flex" }}
                bg="#1A202C"
                color="white"
                _hover={{ bg: "#1A202C" }}
                _active={{ bg: "#1A202C" }}
                size={{ base: "xs", md: "sm", lg: "sm" }}
                rightIcon={<ChevronDownIcon />}
                mr={4}
              >
                <Translate text="Chat Settings" />
              </MenuButton>
              <MenuList>
                <MenuItem
                  icon={<Icon as={FaComments} />}
                  fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                  onClick={newChat}
                >
                  <Translate text="New Chat" />
                </MenuItem>
                {/* <MenuItem
                  icon={<Icon as={FaMapMarkedAlt} />}
                  fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                  isDisabled={!conversationId}
                  onClick={handleSaveTrip}
                >
                  <Translate text="Save Trip" />
                </MenuItem> */}

                <MenuItem
                  icon={<Icon as={FaSave} />}
                  fontSize={{ base: "xs", md: "sm", lg: "sm" }}
                  isDisabled={!conversationId}
                  onClick={() => {
                    const isAuthenticated = !!localStorage.getItem(
                      "REACT_TOKEN_AUTH_KEY"
                    );
                    if (!isAuthenticated) {
                      setSignUpOpen(true);
                    } else {
                      setSaveOpen(true);
                      setErrors("");
                    }
                  }}
                >
                  <Translate text="Save Chat" />
                </MenuItem>
              </MenuList>
            </Menu>

            <SettingsMenu />
          </HStack>
        </Box>
      </Box>
      <AuthModal
        isOpen={isSignUpOpen}
        onClose={() => setSignUpOpen(false)}
        redirectPath="/ai-travel-planner/chat"
      />
      <Modal
        isOpen={isSaveOpen}
        onClose={() => setSaveOpen(false)}
        size={{ base: "sm", md: "md" }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton borderRadius="full" bg="white" />
          <ModalHeader>
            {" "}
            <Translate text="Save Chat" />
          </ModalHeader>
          <ModalBody>
            <Box>
              <form onSubmit={saveChat}>
                <FormControl id="name" isRequired>
                  <Input placeholder="Chat Name" borderRadius="lg" size="md" />
                  <Text color="red.500">{errors}</Text>
                </FormControl>

                {/* Button aligned to right */}
                <Flex justify="flex-end" mt={4}>
                  <Button
                    type="submit"
                    bg="#286328"
                    color="white"
                    _hover={{ bg: "#286328" }}
                    borderRadius="lg"
                    size="md"
                    isDisabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : "Save"}
                  </Button>
                </Flex>
              </form>
            </Box>
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isWarningOpen}
        onClose={() => setWarningOpen(false)}
        size={{ base: "xs", md: "4xl" }}
      >
        <ModalOverlay />
        <ModalContent borderRadius="xl" maxW={{ base: "90%", md: "600px" }}>
          <ModalCloseButton borderRadius="full" bg="white" />
          <ModalBody p={0}>
            <Flex height="100%">
              <Box w="100%" p={8}>
                <Heading as="h1" fontSize="xl" mb={4}>
                  <Translate text="Warning" />
                </Heading>
                <Translate text={warningText} />
                <form onSubmit={handleSaveAndContinue}>
                  <FormControl id="name" isRequired>
                    <Input
                      placeholder="Chat Name"
                      borderRadius="full"
                      size="md"
                    />
                    <Text color="red.500">{warningErrors}</Text>
                  </FormControl>
                  <Flex
                    direction={{ base: "column", md: "row" }}
                    gap={4}
                    mt={4}
                    w="100%"
                  >
                    <Button
                      type="submit"
                      w="100%"
                      bg="grey"
                      color="white"
                      _hover={{ bg: "grey.600" }}
                      borderRadius="full"
                      size="md"
                      isDisabled={warningLoading}
                    >
                      {warningLoading ? <Spinner size="sm" /> : "Save & Continue"}
                    </Button>
                    <Button
                      w="100%"
                      colorScheme="red"
                      color="white"
                      borderRadius="full"
                      size="md"
                      isDisabled={warningLoading}
                      onClick={() => {
                        warningFunction();
                        setWarningOpen(false);
                      }}
                    >
                      {warningLoading ? <Spinner size="sm" /> : "Continue"}
                    </Button>
                  </Flex>
                </form>
              </Box>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        size={{ base: "xs", md: "md" }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton borderRadius="full" bg="white" />
          <ModalHeader>
            <Translate text="Delete Chat" />
          </ModalHeader>
          <ModalBody>
            <Text>
              <Translate text="Are you sure you want to delete" />{" "}
              <strong>"{deleteChatName}"</strong>?{" "}
              <Translate text="This action cannot be undone." />
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsDeleteOpen(false)}
            >
              <Translate text="Cancel" />
            </Button>
            <Button
              colorScheme="red"
              onClick={() => {
                deleteChat(deleteChatId);
                setIsDeleteOpen(false);
              }}
            >
              <Translate text="Delete" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChatHeader;
